import dayjs from 'dayjs';
import { clampDateRange } from '@prerender/design-system/DateRangePicker';

import type { FiltersUpdate } from '~components/Filters';
import { timeFrameForAnalytics } from '~components/Filters/dateRangeLabel';
import {
  type CrawlerCategoryScheme,
  type QueryableCrawlerFilter,
  fromStored,
  toCrawlerSelectedEvent,
} from '~features/crawler-filter';
import { type DashboardV3QueryStrings } from './QueryParameters';
import { type DashboardV3SavedFilters } from './DashboardV3SavedFilters';
import * as Storage from './Storage';

export type DashboardV3FiltersInput = {
  queryStrings: DashboardV3QueryStrings;
  crawlerFilter: QueryableCrawlerFilter;
  savedFilters: DashboardV3SavedFilters | null;
  restoreSavedDomain: boolean;
  dataRetentionDays?: number;
  allCrawlersList: string[];
  defaultDateFrom: Date;
  defaultDateTo: Date;
  categoryScheme: CrawlerCategoryScheme;
};

export type DashboardV3FiltersOutput = {
  paramsToOverride: FiltersUpdate;
  analyticsData: {
    selected_crawlers: string[];
    time_frame: string;
    selected_domain: string;
  };
};

/**
 * Computes the filter parameters to override based on query strings and saved filters.
 * This function handles:
 * - Loading saved filters when query strings are missing
 * - Adjusting date ranges based on data retention days
 * - Computing analytics tracking data
 *
 * @param input - The input parameters including query strings, saved filters, and configuration
 * @returns The computed parameters to override and analytics data
 */
export function computeDashboardV3Filters(input: DashboardV3FiltersInput): DashboardV3FiltersOutput {
  const {
    queryStrings,
    crawlerFilter,
    savedFilters,
    restoreSavedDomain,
    dataRetentionDays,
    allCrawlersList,
    defaultDateFrom,
    defaultDateTo,
    categoryScheme,
  } = input;
  const paramsToOverride: FiltersUpdate = {};

  const savedCrawlerFilter = fromStored(savedFilters, categoryScheme);
  const useSavedCrawlers = crawlerFilter.kind === 'all' && savedCrawlerFilter.kind !== 'all';
  if (useSavedCrawlers) paramsToOverride.crawlerFilter = savedCrawlerFilter;

  paramsToOverride.domain = restoreSavedDomain ? savedFilters?.domain : queryStrings.domain;

  // Handle dates: adjust based on retention days
  if (!queryStrings.dateFrom || !queryStrings.dateTo) {
    if (savedFilters?.dateFrom && savedFilters.dateTo) {
      const adjustedDates = clampDateRange(savedFilters.dateFrom, savedFilters.dateTo, dataRetentionDays);
      paramsToOverride.dateFrom = adjustedDates[0];
      paramsToOverride.dateTo = adjustedDates[1];
    } else {
      paramsToOverride.dateFrom = undefined;
      paramsToOverride.dateTo = undefined;
    }
  } else {
    const adjustedDates = clampDateRange(queryStrings.dateFrom, queryStrings.dateTo, dataRetentionDays);
    paramsToOverride.dateFrom = adjustedDates[0];
    paramsToOverride.dateTo = adjustedDates[1];
  }

  // Compute analytics data
  const { selected_crawlers } = toCrawlerSelectedEvent(
    useSavedCrawlers ? savedCrawlerFilter : crawlerFilter,
    allCrawlersList,
    categoryScheme
  );

  let time_frame = timeFrameForAnalytics(dayjs(defaultDateFrom), dayjs(defaultDateTo));
  if (paramsToOverride.dateFrom) {
    time_frame = timeFrameForAnalytics(dayjs(paramsToOverride.dateFrom), dayjs(paramsToOverride.dateTo!));
  } else if (queryStrings.dateFrom) {
    time_frame = timeFrameForAnalytics(dayjs(queryStrings.dateFrom), dayjs(queryStrings.dateTo!));
  }

  const selected_domain = paramsToOverride.domain || queryStrings.domain || 'All domains';

  return {
    paramsToOverride,
    analyticsData: {
      selected_crawlers,
      time_frame,
      selected_domain,
    },
  };
}

/**
 * Loads saved filters from storage for a given user ID.
 *
 * @param userId - The user ID to load filters for
 * @returns The saved filters or null if none exist
 */
export function loadSavedFilters(userId: number): DashboardV3SavedFilters | null {
  return Storage.loadFilters(userId);
}

